import React, { useEffect, lazy, Suspense } from "react";
import { ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";

import "./App.css";
import { ThemeSwitcherProvider, useThemeSwitcher } from "./theme/ThemeContext";
import Page from "./components/Page";
import { ModuleProvider, useModules } from "./ModuleContext";
import PrivateRoute from "./PrivateRoute";

import Dashboard from "./components/pages/Dashboard";
import SecurityPage from "./components/pages/SecurityPage";
import TaskPage from "./components/pages/TasksPage";
import ResultsPage from "./components/pages/ResultsPage";
import WebResults from "./components/pages/WebResults";
import RedditResults from "./components/pages/RedditResults";
// Twitter UI hidden — kept for future re-enable.
// import TwitterTaskPage from "./components/pages/TwitterTaskPage";
import RedditTaskPage from "./components/pages/RedditTaskPage";
import MastodonTaskPage from "./components/pages/MastodonTaskPage";
import MastodonResults from "./components/pages/MastodonResults";
// Telegram UI hidden — kept for future re-enable.
// import TelegramTaskPage from "./components/pages/TelegramTaskPage";
// import TelegramResults from "./components/pages/TelegramResults";
import SettingsPage from "./components/pages/SettingsPage";
// CortexPanel: standalone Cortex UI (OSINT observable analyzers).
// Currently DISABLED — kept for reference / future use.
// import CortexPanel from "./components/pages/CortexPanel";
import LoginPage from "./components/pages/LoginPage";

const ExtractorHome = lazy(() => import("./components/extractor/ExtractorHome"));
const NexusWorkspace = lazy(() => import("./components/ontology/NexusWorkspace"));
const TaxonomiesPage = lazy(() => import("./components/pages/TaxonomiesPage"));

function AppRoutes() {
  const { modules, loaded } = useModules();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />

      {modules.ids && (
        <Route
          path="/security"
          element={
            <PrivateRoute>
              <SecurityPage />
            </PrivateRoute>
          }
        />
      )}

      {modules.crawlers && (
        <Route
          path="/tasks"
          element={
            <PrivateRoute>
              <TaskPage />
            </PrivateRoute>
          }
        />
      )}

      {(modules.crawlers || modules.social_crawlers) && (
        <Route
          path="/results"
          element={
            <PrivateRoute>
              <ResultsPage />
            </PrivateRoute>
          }
        />
      )}

      {modules.crawlers && (
        <Route
          path="/web/results"
          element={
            <PrivateRoute>
              <WebResults />
            </PrivateRoute>
          }
        />
      )}

      {modules.social_crawlers && (
        <Route
          path="/reddit/results"
          element={
            <PrivateRoute>
              <RedditResults />
            </PrivateRoute>
          }
        />
      )}

      {/* Twitter route hidden — kept for future re-enable.
      {modules.social_crawlers && (
        <Route
          path="/twitter/tasks"
          element={
            <PrivateRoute>
              <TwitterTaskPage />
            </PrivateRoute>
          }
        />
      )}
      */}

      {modules.social_crawlers && (
        <Route
          path="/reddit/tasks"
          element={
            <PrivateRoute>
              <RedditTaskPage />
            </PrivateRoute>
          }
        />
      )}

      {modules.social_crawlers && (
        <Route
          path="/mastodon/tasks"
          element={
            <PrivateRoute>
              <MastodonTaskPage />
            </PrivateRoute>
          }
        />
      )}

      {modules.social_crawlers && (
        <Route
          path="/mastodon/results"
          element={
            <PrivateRoute>
              <MastodonResults />
            </PrivateRoute>
          }
        />
      )}

      {/* Telegram routes hidden — kept for future re-enable.
      {modules.social_crawlers && (
        <Route
          path="/telegram/tasks"
          element={
            <PrivateRoute>
              <TelegramTaskPage />
            </PrivateRoute>
          }
        />
      )}

      {modules.social_crawlers && (
        <Route
          path="/telegram/results"
          element={
            <PrivateRoute>
              <TelegramResults />
            </PrivateRoute>
          }
        />
      )}
      */}

      {/*
        DISABLED: Standalone Cortex OSINT panel route.
        Re-enable if a direct Cortex panel is needed again.

        <Route
          path="/osint"
          element={
            <PrivateRoute>
              <CortexPanel />
            </PrivateRoute>
          }
        />
      */}

      {modules.ontologies && (
        <Route
          path="/ontologies"
          element={
            <PrivateRoute>
              <Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
                <NexusWorkspace />
              </Suspense>
            </PrivateRoute>
          }
        />
      )}

      {modules.taxonomies && (
        <Route
          path="/taxonomies"
          element={
            <PrivateRoute>
              <Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
                <TaxonomiesPage />
              </Suspense>
            </PrivateRoute>
          }
        />
      )}

      {modules.extractor && (
        <Route
          path="/extractor"
          element={
            <PrivateRoute>
              <Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
                <ExtractorHome />
              </Suspense>
            </PrivateRoute>
          }
        />
      )}

      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <SettingsPage />
          </PrivateRoute>
        }
      />

      {loaded && <Route path="*" element={<Navigate to="/" replace />} />}
    </Routes>
  );
}

function AppInner() {
  const { theme, branding } = useThemeSwitcher();
  useEffect(() => { document.title = branding.title; }, [branding]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ModuleProvider>
        <Page>
          <AppRoutes />
        </Page>
      </ModuleProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeSwitcherProvider>
        <AppInner />
      </ThemeSwitcherProvider>
    </BrowserRouter>
  );
}

export default App;
